// Sign-in and the signed-in user, the same steps as App admin and the Lender Hub: email + password, then (only for
// someone who has set it up) the 6-digit code from an authenticator app. The answer to every sign-in step is the
// Session; its `stage` says what comes next. Rule `optional` (default): 2-step is each person's choice on My
// account; `required`: a person without it sets it up at sign-in.
import { z } from 'zod'
import { Id, IsoDateTime } from './common'

export const Role = z.enum(['admin', 'campaign_manager', 'approver', 'analyst'])
export type Role = z.infer<typeof Role>

export const UserStatus = z.enum(['active', 'disabled'])
export type UserStatus = z.infer<typeof UserStatus>

/** A member of our team (not a lead: names here are staff names). */
export const User = z.object({
  id: Id,
  name: z.string(),
  email: z.email(),
  role: Role,
  status: UserStatus,
  /** 2-step sign-in is set up (an authenticator app is linked) */
  two_step: z.boolean(),
  /** a one-time password from an admin: they choose their own at the next sign-in */
  must_change_password: z.boolean(),
  last_signin_at: IsoDateTime.nullable(),
})
export type User = z.infer<typeof User>

export const TwoStepRule = z.enum(['optional', 'required'])
export type TwoStepRule = z.infer<typeof TwoStepRule>

/**
 * What the sign-in needs next: `mfa` the 6-digit code (only for someone with 2-step on) · `change_password` the
 * first sign-in with a one-time password · `mfa_setup` set up 2-step (only with the rule `required`) · `ok` in.
 */
export const SessionStage = z.enum(['mfa', 'change_password', 'mfa_setup', 'ok'])
export type SessionStage = z.infer<typeof SessionStage>

export const Session = z.object({
  stage: SessionStage,
  user: User,
  two_step_rule: TwoStepRule,
  /** only in the answer that turns 2-step on: shown once, never again */
  recovery_codes: z.array(z.string()).optional(),
})
export type Session = z.infer<typeof Session>

export const SignInInput = z.object({
  email: z.email('Type your work email'),
  password: z.string().min(1, 'Type your password'),
})
export type SignInInput = z.infer<typeof SignInInput>

const SixDigits = z.string().regex(/^\d{6}$/, 'The code has 6 digits')

/** The 6-digit code, or one recovery code instead (each works once). */
export const VerifyTwoStepInput = z.union([
  z.object({ code: SixDigits }),
  z.object({ recovery_code: z.string().trim().min(8, 'A recovery code has at least 8 characters') }),
])
export type VerifyTwoStepInput = z.infer<typeof VerifyTwoStepInput>

export const PASSWORD_MIN = 12

export const ChangePasswordInput = z.object({
  current: z.string().min(1, 'Type your current password'),
  next: z.string().min(PASSWORD_MIN, `At least ${PASSWORD_MIN} characters`),
})
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>

/** Step 1 of turning 2-step on: the key for the authenticator app (shown as a QR code and as text). */
export const TwoStepSetup = z.object({ secret: z.string(), otpauth_uri: z.string() })
export type TwoStepSetup = z.infer<typeof TwoStepSetup>

export const CodeInput = z.object({ code: SixDigits })
export type CodeInput = z.infer<typeof CodeInput>

export const PasswordInput = z.object({ password: z.string().min(1, 'Type your password') })
export type PasswordInput = z.infer<typeof PasswordInput>

export const RecoveryCodes = z.object({ recovery_codes: z.array(z.string()) })
export type RecoveryCodes = z.infer<typeof RecoveryCodes>

export interface SessionApi {
  /** null when nobody is signed in or half-way through signing in */
  getSession(): Promise<Session | null>
  signIn(input: SignInInput): Promise<Session>
  verifyTwoStep(input: VerifyTwoStepInput): Promise<Session>
  changePassword(input: ChangePasswordInput): Promise<Session>
  /** a new key each time; it counts only once confirmed with a code */
  setUpTwoStep(): Promise<TwoStepSetup>
  /** turns 2-step on; the answer carries the recovery codes, shown once */
  confirmTwoStep(input: CodeInput): Promise<Session>
  /** new codes (the old ones stop working); needs a current code */
  renewRecoveryCodes(input: CodeInput): Promise<RecoveryCodes>
  /** needs the password; refused when the rule is `required` */
  disableTwoStep(input: PasswordInput): Promise<Session>
  signOut(): Promise<void>
}
